/**
 * <password-gate> custom element
 * UX gate (NOT real security): valida senha via SHA-256, libera conteudo abaixo via localStorage.
 * Padrao do tema Prestige (HTMLElement + customElements.define).
 *
 * Uso:
 *   <password-gate
 *     data-password-hash="<sha256 hex>"
 *     data-storage-key="lesensier:lancamento:unlocked">
 *     <form data-gate-form>...</form>
 *     <div data-locked-content hidden>...</div>
 *   </password-gate>
 */
(function () {
  if (window.customElements.get('password-gate')) return;

  class PasswordGate extends HTMLElement {
    constructor() {
      super();
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      this.passwordHash = (this.dataset.passwordHash || '').toLowerCase();
      this.storageKey = this.dataset.storageKey || 'password-gate:unlocked';
      this.form = this.querySelector('[data-gate-form]');
      this.input = this.querySelector('[data-gate-input]');
      this.errorEl = this.querySelector('[data-gate-error]');
      this.lockedContent = this.querySelector('[data-locked-content]');
      this.gateUI = this.querySelector('[data-gate-ui]');
      this.visibilityToggle = this.querySelector('[data-gate-toggle]');

      if (this.isUnlocked()) {
        this.reveal();
      }

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit);
      }
      if (this.visibilityToggle) {
        this.visibilityToggle.addEventListener('click', this.togglePasswordVisibility);
      }
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.visibilityToggle) {
        this.visibilityToggle.removeEventListener('click', this.togglePasswordVisibility);
      }
    }

    togglePasswordVisibility = () => {
      if (!this.input || !this.visibilityToggle) return;
      const shouldShow = this.input.type === 'password';
      this.input.type = shouldShow ? 'text' : 'password';
      this.visibilityToggle.setAttribute('aria-pressed', String(shouldShow));
      this.visibilityToggle.setAttribute(
        'aria-label',
        shouldShow ? this.visibilityToggle.dataset.hideLabel : this.visibilityToggle.dataset.showLabel
      );
    };

    isUnlocked() {
      try {
        return window.localStorage.getItem(this.storageKey) === '1';
      } catch (e) {
        return false;
      }
    }

    persistUnlock() {
      try {
        window.localStorage.setItem(this.storageKey, '1');
      } catch (e) {
        // ignore (private mode etc)
      }
    }

    async hash(value) {
      const buffer = new TextEncoder().encode(value);
      const digest = await crypto.subtle.digest('SHA-256', buffer);
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.input) return;

      const value = (this.input.value || '').trim();
      if (!value) {
        this.showError(true);
        return;
      }

      const computed = await this.hash(value);
      if (computed === this.passwordHash) {
        this.persistUnlock();
        this.reveal();
      } else {
        this.showError(true);
        this.input.focus();
        this.input.select();
      }
    }

    showError(show) {
      if (!this.errorEl) return;
      if (show) {
        this.errorEl.removeAttribute('hidden');
        this.errorEl.setAttribute('aria-live', 'polite');
      } else {
        this.errorEl.setAttribute('hidden', '');
      }
    }

    reveal() {
      if (this.gateUI) this.gateUI.setAttribute('hidden', '');
      if (this.lockedContent) {
        this.lockedContent.removeAttribute('hidden');
      }
      // Permite que sections subsequentes (lancamento-products, shop-the-look)
      // saibam que o gate liberou — emite evento custom no document SEMPRE,
      // independente de existir [data-locked-content] dentro do <password-gate>.
      document.dispatchEvent(new CustomEvent('password-gate:unlocked', {
        detail: { storageKey: this.storageKey }
      }));
      this.showError(false);
      this.setAttribute('data-state', 'unlocked');
    }
  }

  window.customElements.define('password-gate', PasswordGate);

  /**
   * Helper global: outras sections rendam um wrapper `[data-gated-by="<storageKey>"]`
   * e este script revela quando o evento for disparado OU se ja houver localStorage.
   */
  function revealGatedSections() {
    const wrappers = document.querySelectorAll('[data-gated-by]');
    wrappers.forEach((el) => {
      const key = el.getAttribute('data-gated-by');
      try {
        if (window.localStorage.getItem(key) === '1') {
          el.removeAttribute('hidden');
        }
      } catch (e) { /* ignore */ }
    });
  }

  document.addEventListener('DOMContentLoaded', revealGatedSections);
  document.addEventListener('password-gate:unlocked', (event) => {
    const key = event.detail && event.detail.storageKey;
    if (!key) return;
    document.querySelectorAll('[data-gated-by="' + key + '"]').forEach((el) => {
      el.removeAttribute('hidden');
    });
  });
})();
